import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWorkout } from '../hooks/useWorkouts';
import { useExercises } from '../hooks/useExercises';
import { useClients } from '../hooks/useClients';
import { useWorkoutExercises } from '../hooks/useWorkoutExercises';
import { useMuscleGroups } from '../hooks/useExercises';
import { useInputFocus } from '../hooks/useInputFocus';
import { useDefaultExerciseSettings } from '../hooks/useDefaultExerciseSettings';
import { useClientExceptions } from '../hooks/useClientExceptions';
import { Workout, Client, Exercise, WorkoutExercise, WorkoutExerciseCreate, WorkoutExerciseUpdate, ExerciseLogCreate, ExerciseLog, ClientExerciseExceptionCreate, ClientExerciseException, ClientPackage } from '../types';
import apiService from '../services/api';
import { packagesApi } from '../services/packages';
import toast from 'react-hot-toast';
import { formatPhoneDisplay } from '../utils/formatPhone';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  ArrowLeft,
  Plus,
  CheckCircle,
  Clock,
  Users,
  Calendar,
  User,
  Dumbbell,
  Check,
  X,
  Trash2,
  Info,
  Edit,
  BarChart3,
  Repeat,
  Weight,
  Copy,
  History,
  Layers,
  AlertTriangle,
  Save,
  Download,
  Share2,
  Package
} from 'lucide-react';
import Navigation from '../components/Navigation';
import ExerciseInfoModal from '../components/ExerciseInfoModal';
import EditWorkoutExerciseModal from '../components/EditWorkoutExerciseModal';
import ExerciseStatsModal from '../components/ExerciseStatsModal';
import WeightAdjuster from '../components/WeightAdjuster';
import CopyExercisesModal from '../components/CopyExercisesModal';
import AddExceptionModal from '../components/AddExceptionModal';
import SaveTemplateModal from '../components/SaveTemplateModal';
import ImportTemplateModal from '../components/ImportTemplateModal';
import ShareExercisesButton from '../components/ShareExercisesButton';
import { useQueryClient } from 'react-query';
import { 
  saveClientProgress, 
  getClientProgress, 
  clearClientCache, 
  clearExerciseProgress,
  clearWorkoutCache 
} from '../utils/clientProgressCache';

// Простой модальный компонент
const SimpleModal: React.FC<{ isOpen: boolean; onClose: () => void; children: React.ReactNode }> = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded shadow-lg p-4 min-w-[300px] relative">
        <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={onClose}>&times;</button>
        {children}
      </div>
    </div>
  );
};

const WorkoutPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { trainer, logout } = useAuth();
  const queryClient = useQueryClient();
  const { workout, isLoading: isLoadingWorkout, refetch, updateWorkout, completeWorkout, deleteWorkout, isUpdateLoading, isCompleteLoading, isDeleteLoading } = useWorkout(Number(id));
  const { exercises, isLoading: isExercisesLoading } = useExercises({ limit: 1000 });
  const { clients, isLoading: isClientsLoading } = useClients();
  const { muscleGroups, isLoading: isMuscleGroupsLoading } = useMuscleGroups();

  // Состояние для добавления упражнений
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [isAddingExercise, setIsAddingExercise] = useState(false);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<number[]>([]);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState('');
  const [showExerciseDropdown, setShowExerciseDropdown] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [exerciseForm, setExerciseForm] = useState({
    sets: 3,
    reps: 10,
    weight: 0,
    rest_seconds: 60,
    notes: '',
  });
  const [clientPackages, setClientPackages] = useState<ClientPackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [workoutPackageConsumption, setWorkoutPackageConsumption] = useState<any[]>([]);

  const { 
    addExercise, 
    updateExercise,
    deleteExercise, 
    createExerciseLog, 
    deleteExerciseLog,
    updateExerciseLog, // новый хук
    moveExerciseUp,
    moveExerciseDown,
    isAdding, 
    isUpdating,
    isDeleting, 
    isCreatingLog 
  } = useWorkoutExercises(Number(id), workout, selectedClient);

  // Настройки упражнений по умолчанию
  const { settings: defaultSettings } = useDefaultExerciseSettings();

  // Обработчик выделения текста при фокусе на числовых полях
  const handleInputFocus = useInputFocus();

  // Проверяем, есть ли удаленные упражнения в тренировке
  const hasDeletedExercises = workout?.workout_exercises?.some(we => {
    // Упражнение считается удаленным, если:
    // 1. exercise_id есть, но exercise отсутствует (null/undefined)
    // 2. exercise_id есть, но exercise.name отсутствует
    // 3. exercise_id отсутствует (null) - это тоже признак удаленного упражнения
    return (!we.exercise_id) || (we.exercise_id && (!we.exercise || !we.exercise.name));
  }) || false;
  


  // Состояние для модального окна информации об упражнении
  const [isExerciseInfoModalOpen, setIsExerciseInfoModalOpen] = useState(false);
  const [selectedExerciseForInfo, setSelectedExerciseForInfo] = useState<Exercise | null>(null);

  // Состояние для модального окна редактирования упражнения
  const [isEditExerciseModalOpen, setIsEditExerciseModalOpen] = useState(false);
  const [selectedExerciseForEdit, setSelectedExerciseForEdit] = useState<WorkoutExercise | null>(null);

  // Состояние для модального окна статистики упражнения
  const [isExerciseStatsModalOpen, setIsExerciseStatsModalOpen] = useState(false);
  const [selectedExerciseForStats, setSelectedExerciseForStats] = useState<Exercise | null>(null);
  const [exerciseStatsLogs, setExerciseStatsLogs] = useState<ExerciseLog[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  // Состояния для добавления клиента
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [selectedClientsToAdd, setSelectedClientsToAdd] = useState<Client[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Состояние для модального окна прошлых упражнений
  const [isPastExercisesModalOpen, setIsPastExercisesModalOpen] = useState(false);
  const [pastExercises, setPastExercises] = useState<Workout[]>([]);
  const [isLoadingPastExercises, setIsLoadingPastExercises] = useState(false);

  // Состояние для модального окна исключений
  const [isAddExceptionModalOpen, setIsAddExceptionModalOpen] = useState(false);
  const [selectedExerciseForException, setSelectedExerciseForException] = useState<Exercise | null>(null);

  // Состояние для модального окна сохранения шаблона
  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // Состояние для модального окна импорта шаблона
  const [isImportTemplateModalOpen, setIsImportTemplateModalOpen] = useState(false);

  // Состояние для упражнений клиента
  const [clientExercises, setClientExercises] = useState<any[]>([]);
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [isUpdatingAfterImport, setIsUpdatingAfterImport] = useState(false);

  // Хук для работы с исключениями клиента
  const { 
    exceptions: clientExceptions, 
    createException, 
    isCreating: isCreatingException,
    deleteException
  } = useClientExceptions(selectedClient?.id || 0);

  // Функция для проверки статуса выполнения упражнения
  const isExerciseCompleted = (exerciseId: number, clientId: number) => {
    // Сначала проверяем в clientExercises (обновленные данные)
    const clientExercise = clientExercises.find(ex => ex.id === exerciseId);
    if (clientExercise?.clientLog) {
      return clientExercise.clientLog.is_completed;
    }
    
    // Проверяем глобальный кеш прогресса
    if (workout) {
      const cachedProgress = getClientProgress(workout.id, clientId, exerciseId);
      if (cachedProgress !== undefined) {
        return cachedProgress;
      }
    }
    
    // Fallback к данным из workout (если clientExercises еще не загружены)
    const workoutExercise = workout?.workout_exercises.find(ex => ex.id === exerciseId);
    return workoutExercise?.exercise_logs?.some((log: any) => 
      log.client_id === clientId && log.is_completed
    ) || false;
  };

  // Функция для вычисления прогресса конкретного клиента (стабильная версия)
  const getClientProgressStable = (clientId: number) => {
    if (!workout) return { percentage: 0, completed: 0, total: 0 };
    
    // Всегда используем данные из workout с учетом глобального кеша
    const clientExercisesFromWorkout = workout.workout_exercises.filter(ex => 
      ex.exercise_logs?.some(log => log.client_id === clientId)
    );
    
    const completedExercisesFromWorkout = clientExercisesFromWorkout.filter(ex => {
      const log = ex.exercise_logs?.find(log => log.client_id === clientId);
      if (!log) return false;
      
      // Проверяем глобальный кеш прогресса
      const cachedProgress = getClientProgress(workout.id, clientId, ex.id);
      return cachedProgress !== undefined ? cachedProgress : log.is_completed;
    });
    
    return {
      percentage: clientExercisesFromWorkout.length > 0 ? Math.round((completedExercisesFromWorkout.length / clientExercisesFromWorkout.length) * 100) : 0,
      completed: completedExercisesFromWorkout.length,
      total: clientExercisesFromWorkout.length
    };
  };

  // Клиенты, которых ещё нет в тренировке
  const availableClients = workout && workout.clients
    ? clients.filter(c => !workout.clients.some(wc => wc.id === c.id))
    : [];

  // Фильтрация клиентов по поисковому запросу
  const filteredAvailableClients = availableClients.filter(client => {
    // Исключаем уже выбранных клиентов
    const isAlreadySelected = selectedClientsToAdd.some(c => c.id === client.id);
    if (isAlreadySelected) return false;
    
    if (!clientSearchTerm.trim()) return true;
    
    const searchTerm = clientSearchTerm.toLowerCase().trim();
    const clientName = `${client.first_name} ${client.last_name}`.toLowerCase();
    const clientPhone = client.phone.toLowerCase();
    
    // Разбиваем поисковый запрос на слова
    const searchWords = searchTerm.split(/\s+/).filter(word => word.length > 0);
    
    // Если нет слов для поиска, показываем всех
    if (searchWords.length === 0) return true;
    
    // Проверяем, что все слова поиска присутствуют в имени или телефоне
    return searchWords.every(word => 
      clientName.includes(word) || clientPhone.includes(word)
    );
  });

  // Обработчик добавления клиентов
  const handleAddClients = () => {
    if (selectedClientsToAdd.length === 0 || !workout) return;
    const newClientIds = [...(workout.clients?.map(c => c.id) || []), ...selectedClientsToAdd.map(c => c.id)];
    updateWorkout({ client_ids: newClientIds }, {
      onSuccess: () => {
        setIsAddClientModalOpen(false);
        setSelectedClientsToAdd([]);
        setClientSearchTerm('');
        setShowClientDropdown(false);
      }
    });
  };

  // Обработчик удаления клиента
  const handleRemoveClient = (clientId: number) => {
    if (!workout || !workout.clients) return;
    if (workout.clients.length <= 1) return; // Не удалять последнего клиента
    if (!window.confirm('Удалить этого клиента из тренировки?')) return;
    
    // Очищаем кеш прогресса для удаляемого клиента
    clearClientCache(workout.id, clientId);
    
    const newClientIds = workout.clients.filter(c => c.id !== clientId).map(c => c.id);
    updateWorkout({ client_ids: newClientIds }, {
      onSuccess: () => {
        if (selectedClient && selectedClient.id === clientId) {
          setSelectedClient(null);
        }
      }
    });
  };

  const exerciseDropdownRef = useRef<HTMLDivElement>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Устанавливаем первого клиента как выбранного по умолчанию
  React.useEffect(() => {
    if (workout && workout.clients.length > 0 && !selectedClient) {
      setSelectedClient(workout.clients[0]);
    }
  }, [workout, selectedClient]);

  // Обработчик кликов вне выпадающих списков
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exerciseDropdownRef.current && !exerciseDropdownRef.current.contains(event.target as Node)) {
        setShowExerciseDropdown(false);
      }
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Функция для загрузки упражнений клиента
  const loadClientExercises = async () => {
    if (!selectedClient || !workout) {
      setClientExercises([]);
      return;
    }

    setIsLoadingExercises(true);
    
    let exerciseOrder: any[] = [];
    
    try {
      exerciseOrder = await apiService.getClientExerciseOrder(workout.id, selectedClient.id);

      // Создаем массив упражнений с правильным порядком
      const orderedExercises = exerciseOrder.map((orderItem: any) => {
        // Сначала ищем в локальном состоянии
        let existingExercise = clientExercises.find(ex => ex.id === orderItem.workout_exercise_id);
        
        if (existingExercise) {
          // Используем данные из локального состояния
          return {
            ...existingExercise,
            displayOrder: orderItem.order_index
          };
        }
        
        // Если не найдено в локальном состоянии, ищем в workout.workout_exercises
        const workoutExercise = workout.workout_exercises.find(
          (we: any) => we.id === orderItem.workout_exercise_id
        );
        
        if (workoutExercise) {
          const clientLog = workoutExercise.exercise_logs?.find(
            (log: any) => log.client_id === selectedClient.id
          );
          
          // Проверяем глобальный кеш прогресса
          const cachedProgress = getClientProgress(workout.id, selectedClient.id, orderItem.workout_exercise_id);
          
          return {
            ...workoutExercise,
            clientLog: clientLog ? {
              ...clientLog,
              is_completed: cachedProgress !== undefined ? cachedProgress : clientLog.is_completed
            } : clientLog,
            displayOrder: orderItem.order_index
          };
        }
        
        // Если упражнение не найдено в workout.workout_exercises, но есть в API,
        // это может быть новое упражнение после импорта шаблона
        // Используем данные из API для создания временного объекта
        
        // Если упражнение не найдено ни в локальном состоянии, ни в workout, 
        // создаем временный объект для отображения с данными из API
        const tempExercise = {
          id: orderItem.workout_exercise_id,
          exercise_id: orderItem.exercise_id,
          exercise: {
            id: orderItem.exercise_id,
            name: orderItem.exercise_name,
            description: '',
            muscle_groups: []
          },
          sets: orderItem.sets || 3,
          reps: orderItem.reps || 12,
          weight: orderItem.weight !== null && orderItem.weight !== undefined ? orderItem.weight : 0,
          rest_seconds: 90,
          notes: '',
          clientLog: {
            id: orderItem.log_id,
            client_id: selectedClient.id,
            is_completed: orderItem.is_completed || false,
            order_index: orderItem.order_index
          },
          displayOrder: orderItem.order_index
        };
        
        return tempExercise;
      }).filter(Boolean);

      setClientExercises(orderedExercises);
    } catch (error) {
      console.error('Error loading client exercises:', error);
      // Fallback к старой логике
      const fallbackExercises = workout.workout_exercises.filter(exercise => 
        exercise.exercise_logs?.some(log => log.client_id === selectedClient.id)
      ).map(workoutExercise => {
        // Сначала ищем в локальном состоянии
        let existingExercise = clientExercises.find(ex => ex.id === workoutExercise.id);
        
        if (existingExercise) {
          // Используем данные из локального состояния
          return existingExercise;
        }
        
        // Если не найдено в локальном состоянии, используем данные из workout
        const clientLog = workoutExercise.exercise_logs?.find(log => log.client_id === selectedClient.id);
        
        // Проверяем глобальный кеш прогресса
        const cachedProgress = getClientProgress(workout.id, selectedClient.id, workoutExercise.id);
        
        return {
          ...workoutExercise,
          clientLog: clientLog ? {
            ...clientLog,
            is_completed: cachedProgress !== undefined ? cachedProgress : clientLog.is_completed
          } : clientLog,
          displayOrder: clientLog?.order_index || 0
        };
      }).sort((a, b) => a.displayOrder - b.displayOrder);
      
      // Если fallback логика не нашла упражнений, но API вернул данные,
      // это может быть случай после импорта шаблона
      if (fallbackExercises.length === 0 && exerciseOrder.length > 0) {
        console.log('Fallback логика не нашла упражнений, но API вернул данные. Возможно, данные еще не обновились.');
      }
      
      setClientExercises(fallbackExercises);
    } finally {
      setIsLoadingExercises(false);
    }
  };

  // useEffect для загрузки упражнений клиента
  useEffect(() => {
    loadClientExercises();
  }, [selectedClient?.id, workout?.id]); // Зависим только от ID клиента и тренировки, а не от всего объекта

  // Функция для загрузки пакетов клиента
  const loadClientPackages = async () => {
    if (!selectedClient?.id) {
      setClientPackages([]);
      setSelectedPackageId(null);
      return;
    }

    try {
      const packages = await packagesApi.getClientPackages(selectedClient.id, true); // только активные пакеты

      // Проверяем, есть ли уже назначенный пакет для этого клиента в тренировке
      let selectedPackageId: number | null = null;
      if (workout?.id) {
        const consumption = await apiService.getWorkoutPackageConsumption(workout.id);
        const clientConsumption = consumption.find(c => c.client_id === selectedClient.id);
        if (clientConsumption && clientConsumption.client_package_id) {
          selectedPackageId = clientConsumption.client_package_id;
        }
      }

      // Если выбранный пакет не найден в списке активных, добавляем его
      if (selectedPackageId && !packages.find(pkg => pkg.id === selectedPackageId)) {
        try {
          const allPackages = await packagesApi.getClientPackages(selectedClient.id, false); // все пакеты
          const selectedPackage = allPackages.find(pkg => pkg.id === selectedPackageId);
          if (selectedPackage) {
            packages.push(selectedPackage);
          }
        } catch (error) {
          console.error('Ошибка загрузки выбранного пакета:', error);
        }
      }

      setClientPackages(packages);
      setSelectedPackageId(selectedPackageId);
    } catch (error) {
      console.error('Ошибка загрузки пакетов клиента:', error);
      setClientPackages([]);
      setSelectedPackageId(null);
    }
  };

  // useEffect для загрузки пакетов клиента
  useEffect(() => {
    loadClientPackages();
  }, [selectedClient?.id]);

  // Функция для загрузки текущих назначений пакетов в тренировке
  const loadWorkoutPackageConsumption = async () => {
    if (!workout?.id) return;

    try {
      const consumption = await apiService.getWorkoutPackageConsumption(workout.id);
      setWorkoutPackageConsumption(consumption);

      // Обновляем selectedPackageId на основе текущего назначения
      if (selectedClient?.id) {
        const clientConsumption = consumption.find(c => c.client_id === selectedClient.id);
        if (clientConsumption && clientConsumption.client_package_id) {
          setSelectedPackageId(clientConsumption.client_package_id);
        } else {
          setSelectedPackageId(null);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки назначений пакетов:', error);
    }
  };

  // useEffect для загрузки назначений пакетов
  useEffect(() => {
    loadWorkoutPackageConsumption();
  }, [workout?.id, selectedClient?.id]);

  // Функция для обработки изменения выбранного пакета
  const handlePackageChange = async (newPackageId: number | null) => {
    if (!workout?.id || !selectedClient?.id) return;

    try {
      if (newPackageId === null) {
        // Отменяем списание пакета
        await apiService.removePackageFromWorkoutClient(workout.id, selectedClient.id);
        toast.success('Списание пакета отменено');
      } else {
        // Назначаем новый пакет
        const result = await apiService.assignPackageToWorkoutClient(workout.id, selectedClient.id, newPackageId);
        toast.success(result.message);
      }

      // Перезагружаем данные
      await loadWorkoutPackageConsumption();
      await loadClientPackages(); // Обновляем остатки в пакетах
    } catch (error: any) {
      console.error('Ошибка при изменении пакета:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при изменении пакета');
      // Возвращаем предыдущее значение при ошибке
      await loadWorkoutPackageConsumption();
    }
  };

  // useEffect для очистки кеша при размонтировании компонента
  useEffect(() => {
    return () => {
      // Очищаем кеш прогресса при размонтировании компонента
      if (workout) {
        clearWorkoutCache(workout.id);
      }
    };
  }, [workout?.id]);

  // Автоматическое обновление данных при изменении ссылок
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'share_link_updated') {
        // Обновляем данные тренировки при изменении ссылок
        refetch();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refetch]);

  if (isLoadingWorkout) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Тренировка не найдена</h1>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-700">
            Вернуться на дашборд
          </Link>
        </div>
      </div>
    );
  }

  const handleAddExercise = () => {
    if (!selectedClient || !selectedExercise) return;
    
    // Проверяем, что клиент активен
    if (!selectedClient.is_active) {
      toast.error('Нельзя добавлять упражнения для неактивного клиента');
      return;
    }

    // Всегда создаем новое упражнение в тренировке
    // Это позволяет добавить несколько одинаковых упражнений с разными параметрами (подходы, повторения, вес и т.д.)
    // Например: 3 подхода по 10 повторений, затем 2 подхода по 15 повторений

    const newExercise: WorkoutExerciseCreate = {
      exercise_id: selectedExercise.id,
      sets: exerciseForm.sets,
      reps: exerciseForm.reps,
      weight: exerciseForm.weight || undefined,
      rest_seconds: exerciseForm.rest_seconds,
      notes: exerciseForm.notes,
      order_index: workout.workout_exercises.length,
    };

    // Добавляем упражнение к тренировке
    addExercise(newExercise, {
      onSuccess: (createdExercise) => {
        // После создания упражнения назначаем его клиенту
        const exerciseLog: ExerciseLogCreate = {
          workout_exercise_id: createdExercise.id,
          client_id: selectedClient.id,
          completed_sets: exerciseForm.sets,
          completed_reps: exerciseForm.reps,
          actual_weight: exerciseForm.weight || undefined,
          notes: exerciseForm.notes,
          is_completed: false,
        };

        createExerciseLog({ exerciseId: createdExercise.id, data: exerciseLog }, {
          onSuccess: (createdLog) => {
            // Добавляем новое упражнение в локальное состояние с реальным ID лога
            const newClientExercise = {
              ...createdExercise,
              exercise: selectedExercise,
              clientLog: {
                id: createdLog.id, // Используем реальный ID из сервера
                workout_exercise_id: createdExercise.id,
                client_id: selectedClient.id,
                completed_sets: exerciseForm.sets,
                completed_reps: exerciseForm.reps,
                actual_weight: exerciseForm.weight || undefined,
                notes: exerciseForm.notes,
                is_completed: false,
                order_index: createdLog.order_index || clientExercises.length // Используем order_index из сервера
              },
              displayOrder: createdLog.order_index || clientExercises.length
            };
            
            setClientExercises(prevExercises => [...prevExercises, newClientExercise]);
          }
        });
      }
    });
    
    // Сброс формы
    setSelectedExercise(null);
    setExerciseSearchTerm('');
    setSelectedMuscleGroups([]);
    setOnlyMine(false);
    setShowExerciseDropdown(false);
    setIsAddingExercise(false);
    setExerciseForm({
      sets: 3,
      reps: 10,
      weight: 0,
      rest_seconds: 60,
      notes: '',
    });
  };

  // Новый обработчик удаления упражнения только у выбранного клиента
  const handleDeleteExercise = (exerciseId: number) => {
    if (!selectedClient) return;
    // Найти logId для выбранного клиента и упражнения
    const workoutExercise = workout.workout_exercises.find(ex => ex.id === exerciseId);
    const log = workoutExercise?.exercise_logs?.find(log => log.client_id === selectedClient.id);
    
    if (log && window.confirm('Вы уверены, что хотите удалить это упражнение только у выбранного клиента?')) {
      // Сохраняем удаляемое упражнение для возможного восстановления
      const exerciseToDelete = clientExercises.find(ex => ex.id === exerciseId);
      
      // Очищаем кеш прогресса для этого упражнения
      if (workout) {
        clearExerciseProgress(workout.id, selectedClient.id, exerciseId);
      }
      
      // Сначала обновляем локальное состояние для мгновенного отклика UI
      setClientExercises(prevExercises => 
        prevExercises.filter(exercise => exercise.id !== exerciseId)
      );
      
      // Затем отправляем запрос на сервер
      deleteExerciseLog(log.id, {
        onError: () => {
          // В случае ошибки восстанавливаем упражнение в списке
          if (exerciseToDelete) {
            setClientExercises(prevExercises => [...prevExercises, exerciseToDelete]);
          }
        }
      });
    }
  };

  const handleMarkExerciseCompleted = (exerciseId: number, clientId: number) => {
    // Найти текущее состояние упражнения в clientExercises
    const currentExercise = clientExercises.find(ex => ex.id === exerciseId);
    
    if (currentExercise?.clientLog && currentExercise.clientLog.id && currentExercise.clientLog.id > 0) {
      // Переключаем статус выполнения на основе текущего состояния
      const newStatus = !currentExercise.clientLog.is_completed;
      
      // Сохраняем прогресс в глобальном кеше
      if (workout) {
        saveClientProgress(workout.id, clientId, exerciseId, newStatus);
      }
      
      // Сначала обновляем локальное состояние для мгновенного отклика UI
      setClientExercises(prevExercises => 
        prevExercises.map(exercise => {
          if (exercise.id === exerciseId && exercise.clientLog) {
            return {
              ...exercise,
              clientLog: {
                ...exercise.clientLog,
                is_completed: newStatus
              }
            };
          }
          return exercise;
        })
      );
      
      // Затем отправляем запрос на сервер
      updateExerciseLog({ 
        logId: currentExercise.clientLog.id, 
        data: { is_completed: newStatus },
        suppressToast: false
      }, {
        onSuccess: () => {
          // Успешно обновлено на сервере, кеш уже сохранен
        },
        onError: () => {
          // В случае ошибки откатываем локальное состояние и кеш
          if (workout) {
            saveClientProgress(workout.id, clientId, exerciseId, !newStatus);
          }
          setClientExercises(prevExercises => 
            prevExercises.map(exercise => {
              if (exercise.id === exerciseId && exercise.clientLog) {
                return {
                  ...exercise,
                  clientLog: {
                    ...exercise.clientLog,
                    is_completed: !newStatus
                  }
                };
              }
              return exercise;
            })
          );
        }
      });
    } else {
      // Если лог еще не создан или ID равен 0, показываем сообщение
      toast.error('Лог упражнения еще не создан. Попробуйте через несколько секунд.');
    }
  };

  const handleCompleteClient = (clientId: number) => {
    // Здесь нужно будет добавить API вызов для завершения тренировки клиента

  };

  const handleCompleteAll = () => {
    // Очищаем кеш прогресса для всех клиентов при завершении тренировки
    if (workout) {
      clearWorkoutCache(workout.id);
    }
    completeWorkout();
  };

  const handleDeleteWorkout = () => {
    if (window.confirm('Вы уверены, что хотите удалить эту тренировку? Это действие нельзя отменить.')) {
      // Очищаем кеш прогресса для всех клиентов при удалении тренировки
      if (workout) {
        clearWorkoutCache(workout.id);
      }
      deleteWorkout(undefined, {
        onSuccess: () => {
          navigate('/workouts');
        }
      });
    }
  };

  // Функция для загрузки прошлых упражнений клиента
  const handleShowPastExercises = async () => {
    if (!selectedClient) return;
    
    setIsLoadingPastExercises(true);
    setIsPastExercisesModalOpen(true);
    
    try {
      // Получаем последние тренировки клиента, включая самую новую
      // Используем skip: 0 чтобы включить самую новую тренировку, и limit: 3 для получения 3 последних
      const response = await apiService.getClientPastWorkouts(selectedClient.id, { skip: 0, limit: 3 });
      setPastExercises(response);
    } catch (error) {
      console.error('Ошибка при загрузке предыдущих тренировок:', error);
      toast.error('Ошибка при загрузке предыдущих тренировок');
    } finally {
      setIsLoadingPastExercises(false);
    }
  };

  const handleCleanupDeletedExercises = async () => {

    
    if (!workout || !hasDeletedExercises) {

      return;
    }
    
    if (window.confirm('Удалить все упражнения, которые были удалены из базы данных?')) {

      try {
        const result = await apiService.cleanupDeletedExercises(workout.id);

        toast.success(result.message);
        // Обновляем данные тренировки
        window.location.reload();
      } catch (error) {
        console.error('Cleanup error:', error);
        toast.error('Ошибка при очистке тренировки');
      }
    } else {

    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('ru-RU'),
      time: date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  // Форматирование даты для мобильных (более компактно)
  const formatDateMobile = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: ru });
    } catch {
      return 'Неверная дата';
    }
  };

  const { date, time } = formatDateTime(workout.date);

  // Фильтрация упражнений по выбранным группам мышц, поисковому запросу и фильтру "только мои"
  const filteredExercises = exercises.filter(exercise => {
    // Фильтр "только мои" - показываем только упражнения, созданные текущим тренером
    if (onlyMine && exercise.created_by_trainer_id !== trainer?.id) {
      return false;
    }
    
    // Фильтр по группам мышц (если выбраны группы)
    const matchesMuscleGroups = selectedMuscleGroups.length === 0 || 
      exercise.muscle_group_ids?.some(groupId => selectedMuscleGroups.includes(groupId));
    
    // Если не проходит фильтр по группам мышц, исключаем упражнение
    if (!matchesMuscleGroups) return false;
    
    // Фильтр по поисковому запросу (если введен текст)
    if (!exerciseSearchTerm.trim()) return true;
    
    const searchTerm = exerciseSearchTerm.toLowerCase().trim();
    const exerciseName = exercise.name.toLowerCase();
    const exerciseDescription = exercise.description?.toLowerCase() || '';
    
    // Разбиваем поисковый запрос на слова
    const searchWords = searchTerm.split(/\s+/).filter(word => word.length > 0);
    
    // Если нет слов для поиска, показываем все
    if (searchWords.length === 0) return true;
    
    // Проверяем, что все слова поиска присутствуют в названии или описании
    return searchWords.every(word => 
      exerciseName.includes(word) || exerciseDescription.includes(word)
    );
  });

  // Обработчики для групп мышц
  const handleMuscleGroupToggle = (groupId: number) => {
    setSelectedMuscleGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
    
    // Очищаем выбранное упражнение и поисковый запрос при изменении групп мышц
    setSelectedExercise(null);
    setExerciseSearchTerm('');
    setShowExerciseDropdown(false);
  };

  // Обработчик изменения поискового запроса
  const handleExerciseSearchChange = (value: string) => {
    setExerciseSearchTerm(value);
    setShowExerciseDropdown(true);
    
    if (!value) {
      setSelectedExercise(null);
    }
    
    // Отладочная информация для поиска
    if (value.trim()) {
      // console.log('Поисковый запрос:', value);

    }
  };

  // Обработчик изменения поискового запроса клиентов
  const handleClientSearchChange = (value: string) => {
    setClientSearchTerm(value);
    setShowClientDropdown(true);
  };

  // Обработчик выбора клиента
  const handleClientSelect = (client: Client) => {
    // Проверяем, не выбран ли уже этот клиент
    const isAlreadySelected = selectedClientsToAdd.some(c => c.id === client.id);
    
    if (isAlreadySelected) {
      // Если клиент уже выбран, убираем его из списка
      setSelectedClientsToAdd(prev => prev.filter(c => c.id !== client.id));
    } else {
      // Если клиент не выбран, добавляем его в список
      setSelectedClientsToAdd(prev => [...prev, client]);
    }
    
    // Очищаем поисковый запрос и закрываем выпадающий список
    setClientSearchTerm('');
    setShowClientDropdown(false);
  };

  // Обработчик удаления клиента из выбранных
  const handleRemoveSelectedClient = (clientId: number) => {
    setSelectedClientsToAdd(prev => prev.filter(c => c.id !== clientId));
  };

  // Обработчик очистки выбранных клиентов
  const handleClearSelectedClients = () => {
    setSelectedClientsToAdd([]);
  };

  const handleExerciseSelect = async (exercise: Exercise) => {
    setSelectedExercise(exercise);
    setExerciseSearchTerm(exercise.name);
    setShowExerciseDropdown(false);

    // Получаем вес из истории тренировок для этого упражнения (1-й приоритет)
    let weightFromHistory = 0;
    if (selectedClient) {
      try {
        weightFromHistory = await apiService.getExerciseWeightFromHistory(exercise.id, selectedClient.id);
      } catch (error) {
        // Игнорируем ошибки 404 (история не найдена) - это нормально
        if (error && typeof error === 'object' && 'status_code' in error && error.status_code !== 404) {
          // console.log('Не удалось получить вес из истории:', error);
        }
      }
    }

    // Определяем вес: сначала история, потом настройки (2-й приоритет)
    let finalWeight = weightFromHistory;
    if (finalWeight === 0) {
      finalWeight = defaultSettings.weight;
    }

    // Обновляем форму с настройками по умолчанию
    setExerciseForm({
      sets: defaultSettings.sets,
      reps: defaultSettings.reps,
      weight: finalWeight,
      rest_seconds: defaultSettings.rest_time,
      notes: '',
    });
  };

  const handleShowExerciseInfo = (exercise: Exercise) => {
    setSelectedExerciseForInfo(exercise);
    setIsExerciseInfoModalOpen(true);
  };

  // Обработчик открытия модального окна редактирования упражнения
  const handleEditExercise = (workoutExercise: WorkoutExercise) => {
    setSelectedExerciseForEdit(workoutExercise);
    setIsEditExerciseModalOpen(true);
  };

  // Обработчик сохранения изменений упражнения
  const handleSaveExerciseChanges = (data: WorkoutExerciseUpdate) => {
    if (!selectedExerciseForEdit) return;
    
    updateExercise(
      { exerciseId: selectedExerciseForEdit.id, data },
      {
        onSuccess: (updatedExercise) => {
          // Обновляем локальное состояние для мгновенного отклика UI
          setClientExercises(prevExercises => 
            prevExercises.map(exercise => {
              if (exercise.id === selectedExerciseForEdit.id) {
                return {
                  ...exercise,
                  sets: data.sets !== undefined ? data.sets : exercise.sets,
                  reps: data.reps !== undefined ? data.reps : exercise.reps,
                  weight: data.weight !== undefined ? data.weight : exercise.weight,
                  rest_seconds: data.rest_seconds !== undefined ? data.rest_seconds : exercise.rest_seconds,
                  notes: data.notes !== undefined ? data.notes : exercise.notes,
                };
              }
              return exercise;
            })
          );
          
          // Обновляем фактические данные для всех клиентов, если изменились плановые значения
          const logsToUpdate = selectedExerciseForEdit.exercise_logs || [];
          
          logsToUpdate.forEach(log => {
            const updateData: any = {};
            
            if (data.sets !== undefined) {
              updateData.completed_sets = data.sets;
            }
            if (data.reps !== undefined) {
              updateData.completed_reps = data.reps;
            }
            if (data.weight !== undefined) {
              updateData.actual_weight = data.weight;
            }
            
            // Обновляем только если есть изменения
            if (Object.keys(updateData).length > 0) {
              updateExerciseLog({ logId: log.id, data: updateData, suppressToast: true }, {
                onSuccess: () => {
                  // Обновляем локальное состояние для логов
                  setClientExercises(prevExercises => 
                    prevExercises.map(exercise => {
                      if (exercise.clientLog && exercise.clientLog.id === log.id) {
                        return {
                          ...exercise,
                          clientLog: {
                            ...exercise.clientLog,
                            ...updateData
                          }
                        };
                      }
                      return exercise;
                    })
                  );
                }
              });
            }
          });
          
          setIsEditExerciseModalOpen(false);
          setSelectedExerciseForEdit(null);
        }
      }
    );
  };

  // Обработчик открытия статистики упражнения
  const handleShowExerciseStats = async (exercise: Exercise) => {    
    if (!selectedClient) {
      return;
    }
    
    setSelectedExerciseForStats(exercise);
    setIsLoadingStats(true);
    setIsExerciseStatsModalOpen(true);
    
    try {
      // Загружаем все логи клиента для статистики
      const logs = await apiService.getClientExerciseLogsForStats(selectedClient.id, { limit: 1000 });
      setExerciseStatsLogs(logs);
    } catch (error) {
      console.error('Ошибка при загрузке статистики:', error);
      toast.error('Ошибка при загрузке статистики упражнения');
    } finally {
      setIsLoadingStats(false);
    }
  };



  // Обработчики для исключений упражнений
  const handleAddException = (exercise: Exercise) => {
    // Проверяем, есть ли уже исключение для этого упражнения
    const existingException = clientExceptions.find(
      (exception: ClientExerciseException) => exception.exercise_id === exercise.id
    );
    
    if (existingException) {
      // Если исключение уже есть, удаляем его
      if (window.confirm('Отменить исключение для этого упражнения?')) {
        deleteException(existingException.id);
      }
    } else {
      // Если исключения нет, добавляем его
      setSelectedExerciseForException(exercise);
      setIsAddExceptionModalOpen(true);
    }
  };

  const handleSubmitException = (data: ClientExerciseExceptionCreate) => {
    createException(data, {
      onSuccess: () => {
        setIsAddExceptionModalOpen(false);
        setSelectedExerciseForException(null);
      }
    });
  };

  const handleCloseExceptionModal = () => {
    setIsAddExceptionModalOpen(false);
    setSelectedExerciseForException(null);
  };

  // Проверяем, есть ли исключение для упражнения
  const hasException = (exerciseId: number) => {
    return clientExceptions.some((exception: ClientExerciseException) => exception.exercise_id === exerciseId);
  };

  const getExceptionReason = (exerciseId: number) => {
    const exception = clientExceptions.find((exception: ClientExerciseException) => exception.exercise_id === exerciseId);
    return exception?.reason;
  };

  // Обработчики для сохранения шаблона
  const handleSaveTemplate = () => {
    if (!selectedClient) {
      toast.error('Выберите клиента для сохранения шаблона');
      return;
    }
    setIsSaveTemplateModalOpen(true);
  };

  const handleSaveTemplateSubmit = async (templateName: string, description: string) => {
    if (!selectedClient || !workout) return;

    setIsSavingTemplate(true);
    try {
      await apiService.saveTemplateFromWorkoutExercises(
        workout.id,
        selectedClient.id,
        templateName,
        description
      );
      toast.success('Шаблон успешно сохранен!');
      setIsSaveTemplateModalOpen(false);
    } catch (error: any) {
      const errorMessage = error.detail || 'Ошибка при сохранении шаблона';
      toast.error(errorMessage);
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleCloseSaveTemplateModal = () => {
    setIsSaveTemplateModalOpen(false);
  };

  // Обработчики для импорта шаблона
  const handleImportTemplate = () => {
    if (!selectedClient) {
      toast.error('Выберите клиента для импорта шаблона');
      return;
    }
    setIsImportTemplateModalOpen(true);
  };

  const handleCloseImportTemplateModal = () => {
    setIsImportTemplateModalOpen(false);
  };

  // Обработчики для управления порядком упражнений
  const handleMoveExerciseUp = async (workoutExercise: any) => {
    if (!selectedClient || !workout) return;

    try {
      await moveExerciseUp(workoutExercise.clientLog.id);
      
      // Сохраняем текущие статусы выполнения перед обновлением
      const currentStatuses = new Map();
      clientExercises.forEach(exercise => {
        if (exercise.clientLog) {
          currentStatuses.set(exercise.id, exercise.clientLog.is_completed);
        }
      });

      // Перезагружаем порядок упражнений
      const exerciseOrder = await apiService.getClientExerciseOrder(workout.id, selectedClient.id);
      
      // Создаем массив упражнений с правильным порядком
      const orderedExercises = exerciseOrder.map((orderItem: any) => {
        // Сначала ищем в локальном состоянии
        let existingExercise = clientExercises.find(ex => ex.id === orderItem.workout_exercise_id);
        
        if (existingExercise) {
          // Используем данные из локального состояния
          return {
            ...existingExercise,
            displayOrder: orderItem.order_index
          };
        }
        
        // Если не найдено в локальном состоянии, ищем в workout.workout_exercises
        const workoutExercise = workout.workout_exercises.find(
          (we: any) => we.id === orderItem.workout_exercise_id
        );
        
        if (workoutExercise) {
          const clientLog = workoutExercise.exercise_logs?.find(
            (log: any) => log.client_id === selectedClient.id
          );
          
          // Восстанавливаем локальный статус выполнения
          const localStatus = currentStatuses.get(workoutExercise.id);
          
          return {
            ...workoutExercise,
            clientLog: clientLog ? {
              ...clientLog,
              is_completed: localStatus !== undefined ? localStatus : clientLog.is_completed
            } : clientLog,
            displayOrder: orderItem.order_index
          };
        }
        
        return null;
      }).filter(Boolean);

      setClientExercises(orderedExercises);
      toast.success('Порядок упражнений обновлен');
    } catch (error) {
      console.error('Error moving exercise up:', error);
      toast.error('Ошибка при перемещении упражнения');
    }
  };

  const handleMoveExerciseDown = async (workoutExercise: any) => {
    if (!selectedClient || !workout) return;

    try {
      await moveExerciseDown(workoutExercise.clientLog.id);
      
      // Сохраняем текущие статусы выполнения перед обновлением
      const currentStatuses = new Map();
      clientExercises.forEach(exercise => {
        if (exercise.clientLog) {
          currentStatuses.set(exercise.id, exercise.clientLog.is_completed);
        }
      });

      // Перезагружаем порядок упражнений
      const exerciseOrder = await apiService.getClientExerciseOrder(workout.id, selectedClient.id);
      
      // Создаем массив упражнений с правильным порядком
      const orderedExercises = exerciseOrder.map((orderItem: any) => {
        // Сначала ищем в локальном состоянии
        let existingExercise = clientExercises.find(ex => ex.id === orderItem.workout_exercise_id);
        
        if (existingExercise) {
          // Используем данные из локального состояния
          return {
            ...existingExercise,
            displayOrder: orderItem.order_index
          };
        }
        
        // Если не найдено в локальном состоянии, ищем в workout.workout_exercises
        const workoutExercise = workout.workout_exercises.find(
          (we: any) => we.id === orderItem.workout_exercise_id
        );
        
        if (workoutExercise) {
          const clientLog = workoutExercise.exercise_logs?.find(
            (log: any) => log.client_id === selectedClient.id
          );
          
          // Восстанавливаем локальный статус выполнения
          const localStatus = currentStatuses.get(workoutExercise.id);
          
          return {
            ...workoutExercise,
            clientLog: clientLog ? {
              ...clientLog,
              is_completed: localStatus !== undefined ? localStatus : clientLog.is_completed
            } : clientLog,
            displayOrder: orderItem.order_index
          };
        }
        
        return null;
      }).filter(Boolean);

      setClientExercises(orderedExercises);
      toast.success('Порядок упражнений обновлен');
    } catch (error) {
      console.error('Error moving exercise down:', error);
      toast.error('Ошибка при перемещении упражнения');
    }
  };

  // Функция для перемещения импортированных упражнений в конец списка
  // Функция больше не нужна, так как упражнения из шаблона уже добавляются в конец списка
  // с правильным order_index в backend

  // Проверяем, есть ли активная шаринг-ссылка для выбранного клиента
  const hasActiveShareLink = Boolean(selectedClient && workout.shared_exercises?.some(
    s => s.client_id === selectedClient.id && s.is_active
  ));

  // Callback для обновления состояния после удаления ссылки
  const handleShareDeleted = () => {
    // Обновляем данные тренировки, чтобы пересчитать hasActiveShareLink
    refetch();
  };

  // Callback для обновления состояния после создания ссылки
  const handleShareCreated = () => {
    // Обновляем данные тренировки, чтобы пересчитать hasActiveShareLink
    refetch();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Навигационная панель */}
      <Navigation currentPage="workouts" />

      {/* Заголовок страницы */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:py-6 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link
                to="/workouts"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft size={20} className="sm:w-6 sm:h-6" />
              </Link>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">{workout.name}</h1>
                <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-500">
                  <span className="hidden sm:inline">{date} в {time} • {workout.duration_minutes} минут</span>
                  <span className="sm:hidden">{formatDateMobile(workout.date)} {time} • {workout.duration_minutes} мин</span>
                </p>
              </div>
            </div>
            <div className="flex justify-center sm:justify-end space-x-2">
              {hasDeletedExercises && (
                <button
                  onClick={handleCleanupDeletedExercises}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 w-full sm:w-auto"
                >
                  <Trash2 className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Очистить удаленные</span>
                  <span className="sm:hidden">Очистить</span>
                </button>
              )}
              <button
                onClick={handleCompleteAll}
                disabled={isCompleteLoading || workout.is_completed}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                <CheckCircle className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Завершить</span>
                <span className="sm:hidden">Завершить</span>
              </button>
              <button
                onClick={handleDeleteWorkout}
                disabled={isDeleteLoading}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                <Trash2 className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Удалить</span>
                <span className="sm:hidden">Удалить</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Основной контент */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Предупреждение о удаленных упражнениях */}
        {hasDeletedExercises && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-orange-800">
                  Внимание: удаленные упражнения
                </h3>
                <div className="mt-2 text-sm text-orange-700">
                  <p>
                    В этой тренировке есть упражнения, которые были удалены из базы данных. 
                    Вы можете очистить тренировку от этих упражнений, нажав кнопку "Очистить удаленные" в заголовке.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Левая колонка - только клиенты */}
          <div className="lg:col-span-1">
            {/* Список клиентов */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Клиенты</h2>
              
              {/* Кнопка добавить клиента */}
              {workout && availableClients.length > 0 && (
                <button
                  className="mb-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                  onClick={() => setIsAddClientModalOpen(true)}
                >
                  <Plus className="inline-block mr-2 h-4 w-4" /> Добавить клиента
                </button>
              )}
              {/* Модальное окно выбора клиентов */}
              <SimpleModal isOpen={!!isAddClientModalOpen} onClose={() => setIsAddClientModalOpen(false)}>
                {workout && (
                  <div className="p-4">
                    <h2 className="text-lg font-semibold mb-4">Выберите клиентов</h2>
                    
                    {/* Поиск клиента */}
                    <div className="relative mb-4" ref={clientDropdownRef}>
                      <input
                        type="text"
                        value={clientSearchTerm}
                        onChange={(e) => handleClientSearchChange(e.target.value)}
                        onFocus={() => setShowClientDropdown(true)}
                        placeholder="Начните вводить имя или телефон клиента..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      
                      {/* Выпадающий список клиентов */}
                      {showClientDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {filteredAvailableClients.length > 0 ? (
                            filteredAvailableClients.map((client) => {
                              const isSelected = selectedClientsToAdd.some(c => c.id === client.id);
                              return (
                                <div 
                                  key={client.id} 
                                  className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
                                    isSelected ? 'bg-blue-50 border-l-4 border-blue-400' : 'hover:bg-gray-100'
                                  }`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleClientSelect(client)}
                                    className="flex-1 text-left focus:outline-none"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                        isSelected 
                                          ? 'bg-blue-600 border-blue-600' 
                                          : 'border-gray-300'
                                      }`}>
                                        {isSelected && (
                                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </div>
                                      <div>
                                        <div className="font-medium text-gray-900">
                                          {client.first_name} {client.last_name}
                                        </div>
                                                                          <div className="text-sm text-gray-500">
                                    {formatPhoneDisplay(client.phone)}
                                  </div>
                                      </div>
                                    </div>
                                  </button>
                                  {!client.is_active && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2">
                                      Неактивен
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              {clientSearchTerm 
                                ? 'Клиенты не найдены' 
                                : 'Введите имя или телефон для поиска'
                              }
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Выбранные клиенты */}
                    {selectedClientsToAdd.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-medium text-gray-700">
                            Выбранные клиенты ({selectedClientsToAdd.length}):
                          </h3>
                          <button
                            type="button"
                            onClick={handleClearSelectedClients}
                            className="text-sm text-red-600 hover:text-red-800 underline"
                          >
                            Очистить
                          </button>
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedClientsToAdd.map((client) => (
                            <div key={client.id} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-md">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-blue-600 border-2 border-blue-600 rounded flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div>
                                  <div className="font-medium text-blue-900">
                                    {client.first_name} {client.last_name}
                                  </div>
                                  <div className="text-sm text-blue-700">
                                    {formatPhoneDisplay(client.phone)}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!client.is_active && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Неактивен
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSelectedClient(client.id)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
                                  title="Убрать из выбранных"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <button
                        className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        onClick={() => {
                          setIsAddClientModalOpen(false);
                          setClientSearchTerm('');
                          setSelectedClientsToAdd([]);
                          setShowClientDropdown(false);
                        }}
                      >
                        Отмена
                      </button>
                      <button
                        className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 hover:bg-blue-700"
                        disabled={selectedClientsToAdd.length === 0 || isUpdateLoading}
                        onClick={handleAddClients}
                      >
                        Добавить {selectedClientsToAdd.length > 0 ? `(${selectedClientsToAdd.length})` : ''}
                      </button>
                    </div>
                  </div>
                )}
              </SimpleModal>
              <div className="space-y-3">
                {!workout?.clients || workout.clients.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm">
                      Нет клиентов в тренировке
                    </p>
                  </div>
                ) : (
                  <>
                    {workout.clients.map((client) => (
                      <div 
                        key={client.id} 
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedClient?.id === client.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedClient(client)}
                      >
                        <div className="flex items-center space-x-3 w-full">
                          <div className={`rounded-full p-2 ${
                            selectedClient?.id === client.id 
                              ? 'bg-blue-500' 
                              : 'bg-blue-100'
                          }`}>
                            <User size={16} className={selectedClient?.id === client.id ? 'text-white' : 'text-blue-600'} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium ${
                                selectedClient?.id === client.id ? 'text-blue-900' : 'text-gray-900'
                              }`}>
                                {client.first_name} {client.last_name}
                              </p>
                              {!client.is_active && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Неактивен
                                </span>
                              )}
                            </div>
                            <p className={`text-xs ${
                              selectedClient?.id === client.id ? 'text-blue-700' : 'text-gray-500'
                            }`}>
                              {formatPhoneDisplay(client.phone)}
                            </p>
                            {/* Прогресс выполнения упражнений */}
                            <div className="mt-1">
                              <div className="flex items-center space-x-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                    style={{ 
                                      width: `${getClientProgressStable(client.id).percentage}%` 
                                    }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {`${getClientProgressStable(client.id).completed}/${getClientProgressStable(client.id).total}`}
                                </span>
                              </div>
                            </div>
                          </div>
                          {/* Кнопка удалить клиента (если больше 1 клиента) */}
                          {workout.clients.length > 1 && (
                            <button
                              className="ml-auto p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              title="Удалить клиента из тренировки"
                              onClick={e => {
                                e.stopPropagation();
                                handleRemoveClient(client.id);
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="mt-6" />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Упражнения выбранного клиента */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                {/* Заголовок секции с двумя блоками */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0 mb-3">
                  {/* Левый блок: заголовок и иконки */}
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">
                      <span className="hidden sm:inline">
                        Упражнения {selectedClient ? `для ${selectedClient.first_name} ${selectedClient.last_name}` : 'тренировки'}
                      </span>
                      <span className="sm:hidden">
                        Упражнения {selectedClient ? `для ${selectedClient.first_name}` : 'тренировки'}
                      </span>
                    </h2>

                    {/* Иконки действий */}
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                      <div className="flex items-center">
                    {selectedClient && workout.clients.length > 1 && (
                    <div className="flex flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={handleImportTemplate}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="Импортировать шаблон"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleSaveTemplate}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        title="Сохранить как шаблон"
                      >
                        <Save className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setIsCopyModalOpen(true)}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        title="Копировать клиенту"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleShowPastExercises}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        title="История упражнений"
                      >
                        <History className="h-5 w-5" />
                      </button>
                      <ShareExercisesButton
                        workoutId={workout.id}
                        clientId={selectedClient.id}
                        clientName={`${selectedClient.first_name} ${selectedClient.last_name}`}
                        hasActiveShareLink={hasActiveShareLink}
                        onShareDeleted={handleShareDeleted}
                        onShareCreated={handleShareCreated}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      />
                      <button
                        onClick={() => {
                          if (selectedClient.is_active) {
                            setIsAddingExercise(true);
                          } else {
                            toast.error('Нельзя добавлять упражнения для неактивного клиента');
                          }
                        }}
                        disabled={!selectedClient.is_active}
                        className={`inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          selectedClient.is_active
                            ? 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                            : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                        }`}
                        title={selectedClient.is_active ? "Добавить упражнение" : "Клиент неактивен"}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                  {selectedClient && workout.clients.length <= 1 && (
                    <div className="flex flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={handleImportTemplate}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="Импортировать шаблон"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleSaveTemplate}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        title="Сохранить как шаблон"
                      >
                        <Save className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleShowPastExercises}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        title="История упражнений"
                      >
                        <History className="h-5 w-5" />
                      </button>
                      <ShareExercisesButton
                        workoutId={workout.id}
                        clientId={selectedClient.id}
                        clientName={`${selectedClient.first_name} ${selectedClient.last_name}`}
                        hasActiveShareLink={hasActiveShareLink}
                        onShareDeleted={handleShareDeleted}
                        onShareCreated={handleShareCreated}
                        className="inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      />
                      <button
                        onClick={() => {
                          if (selectedClient.is_active) {
                            setIsAddingExercise(true);
                          } else {
                            toast.error('Нельзя добавлять упражнения для неактивного клиента');
                          }
                        }}
                        disabled={!selectedClient.is_active}
                        className={`inline-flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          selectedClient.is_active
                            ? 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                            : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                        }`}
                        title={selectedClient.is_active ? "Добавить упражнение" : "Клиент неактивен"}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                      </div>
                    </div>
                  </div>

                  {/* Правый блок: списание пакетов */}
                  {selectedClient && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:ml-4 flex-shrink-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <Package size={16} className="text-indigo-600" />
                        <span className="text-xs font-medium text-gray-700">Списать тренировку:</span>
                      </div>
                      <select
                        value={selectedPackageId || ''}
                        onChange={(e) => {
                          const newPackageId = e.target.value ? Number(e.target.value) : null;
                          setSelectedPackageId(newPackageId);
                          handlePackageChange(newPackageId);
                        }}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1 min-w-[180px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        disabled={clientPackages.length === 0}
                      >
                        {clientPackages.length > 0 ? (
                          <>
                            <option value="">Не списывать</option>
                            {clientPackages.map(pkg => (
                              <option key={pkg.id} value={pkg.id}>
                                {pkg.package?.name} ({(pkg.sessions_remaining ?? 0) > 0 ? `осталось: ${pkg.sessions_remaining}` : 'исчерпан'})
                              </option>
                            ))}
                          </>
                        ) : (
                          <option value="">Нет назначенных пакетов</option>
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </div>


              <div className="p-6">
                {!selectedClient ? (
                  <div className="text-center py-8">
                    <Users size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Выберите клиента
                    </h3>
                    <p className="text-gray-500">
                      Выберите клиента из списка слева, чтобы увидеть его упражнения
                    </p>
                  </div>
                ) : clientExercises.length === 0 ? (
                  <div className="text-center py-8">
                    <Dumbbell size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Нет упражнений
                    </h3>
                    <p className="text-gray-500">
                      У {selectedClient.first_name} пока нет упражнений в этой тренировке
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {isLoadingExercises ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Загрузка упражнений...</p>
                      </div>
                    ) : isUpdatingAfterImport ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Обновление после импорта...</p>
                      </div>
                    ) : (
                      clientExercises.map((workoutExercise, index) => {
                        // Проверяем, есть ли исключение для этого упражнения
                        const isException = selectedClient && workoutExercise.exercise && hasException(workoutExercise.exercise.id);
                        
                        return (
                          <div 
                            key={workoutExercise.id} 
                            className={`border rounded-lg sm:p-4 p-3 transition-all duration-200 ${
                              isException 
                                ? 'border-red-300 bg-red-50 shadow-sm' 
                                : 'border-gray-200'
                            }`}
                          >
                            {/* Основное содержимое упражнения */}
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className={`sm:text-lg text-base font-medium ${
                                    isException ? 'text-red-800' : 'text-gray-900'
                                  }`}>
                                    {index + 1}. {workoutExercise.exercise?.name || 'Упражнение удалено'}
                                  </h3>
                                  {workoutExercise.exercise && (
                                    <>
                                      <button
                                        onClick={() => handleShowExerciseStats(workoutExercise.exercise!)}
                                        className="text-purple-600 hover:text-purple-800 p-1 rounded-full hover:bg-purple-50 transition-colors"
                                        title="Статистика упражнения"
                                      >
                                        <BarChart3 size={16} />
                                      </button>
                                      <button
                                        onClick={() => handleShowExerciseInfo(workoutExercise.exercise!)}
                                        className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors"
                                        title="Информация об упражнении"
                                      >
                                        <Info size={16} />
                                      </button>
                                    </>
                                  )}
                                </div>
                                {!workoutExercise.exercise && (
                                  <p className="text-sm text-red-600 mt-1">
                                    ⚠️ Это упражнение было удалено из базы данных
                                  </p>
                                )}
                                {/* Информация об упражнении - только для десктопа */}
                                <div className={`hidden sm:flex mt-2 items-center space-x-4 text-sm ${
                                  isException ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  <div className="flex items-center">
                                    <Layers size={16} className={`mr-1 ${isException ? 'text-red-500' : 'text-gray-500'}`} />
                                    <span>{workoutExercise.sets}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Repeat size={16} className={`mr-1 ${isException ? 'text-red-500' : 'text-gray-500'}`} />
                                    <span>{workoutExercise.reps}</span>
                                  </div>
                                  {workoutExercise.weight !== null && workoutExercise.weight !== undefined && (
                                    <div className="flex items-center">
                                      <Weight size={16} className={`mr-1 ${isException ? 'text-red-500' : 'text-gray-500'}`} />
                                      <span>{workoutExercise.weight} кг</span>
                                    </div>
                                  )}
                                </div>
                                {workoutExercise.notes && (
                                  <p className={`mt-2 text-sm ${
                                    isException ? 'text-red-600' : 'text-gray-600'
                                  }`}>
                                    <span className="font-medium">Заметки:</span> {workoutExercise.notes}
                                  </p>
                                )}
                                {isException && workoutExercise.exercise && (
                                  <p className={`mt-2 text-sm ${
                                    isException ? 'text-red-600' : 'text-gray-600'
                                  }`}>
                                    <span className="font-medium">Исключено:</span> {getExceptionReason(workoutExercise.exercise.id)}
                                  </p>
                                )}
                              </div>
                              
                              {/* Кнопки действий - только на десктопе */}
                              <div className="hidden sm:flex flex-col items-center space-y-2">
                                {/* Первый ряд: исключить, изменить, удалить */}
                                <div className="flex items-center space-x-2">
                                  {/* Кнопка исключения */}
                                  {selectedClient && workoutExercise.exercise && (
                                    <button
                                      onClick={() => handleAddException(workoutExercise.exercise!)}
                                      className={`p-2 rounded-md transition-colors ${
                                        hasException(workoutExercise.exercise.id)
                                          ? 'text-green-700 bg-green-100 border border-green-300 hover:bg-green-200'
                                          : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                                      }`}
                                      title={
                                        hasException(workoutExercise.exercise.id)
                                          ? `Отменить исключение: ${getExceptionReason(workoutExercise.exercise.id)}`
                                          : 'Добавить в исключения'
                                      }
                                    >
                                      <AlertTriangle size={18} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditExercise(workoutExercise)}
                                    disabled={isUpdating}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md disabled:opacity-50 transition-colors"
                                    title="Изменить параметры упражнения"
                                  >
                                    <Edit size={18} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteExercise(workoutExercise.id)}
                                    disabled={isDeleting}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md disabled:opacity-50 transition-colors"
                                    title="Удалить упражнение"
                                  >
                                    {isDeleting ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                    ) : (
                                      <Trash2 size={18} />
                                    )}
                                  </button>
                                </div>
                                
                                {/* Второй ряд: выполнить, вверх, вниз */}
                                <div className="flex items-center space-x-2">
                                  {/* Кнопка отметки выполнения - только иконка */}
                                  {selectedClient && (
                                    <button
                                      onClick={() => handleMarkExerciseCompleted(workoutExercise.id, selectedClient.id)}
                                      className={`p-2 rounded-md border transition-all duration-200 flex items-center justify-center ${
                                        isExerciseCompleted(workoutExercise.id, selectedClient.id)
                                          ? 'text-green-700 bg-green-100 border-green-300'
                                          : 'text-gray-500 bg-gray-50 border-gray-200'
                                      }`}
                                      title="Отметить как выполненное"
                                    >
                                      <Check size={18} />
                                    </button>
                                  )}
                                  
                                  {/* Кнопки управления порядком */}
                                  <button
                                    onClick={() => handleMoveExerciseUp(workoutExercise)}
                                    disabled={index === 0}
                                    className={`p-2 rounded-md transition-colors ${
                                      index === 0
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                    title="Переместить вверх"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleMoveExerciseDown(workoutExercise)}
                                    disabled={index === clientExercises.length - 1}
                                    className={`p-2 rounded-md transition-colors ${
                                      index === clientExercises.length - 1
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                    title="Переместить вниз"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Параметры упражнения и кнопки для мобильных устройств */}
                            <div className="sm:hidden mt-2">
                              {/* Параметры упражнения в одну строку с иконками */}
                              <div className={`flex items-center space-x-4 mb-2 ${
                                isException ? 'text-red-600' : ''
                              }`}>
                                <div className="flex items-center">
                                  <Layers size={16} className={`mr-1 ${isException ? 'text-red-500' : 'text-gray-500'}`} />
                                  <span className="text-sm">{workoutExercise.sets}</span>
                                </div>
                                <div className="flex items-center">
                                  <Repeat size={16} className={`mr-1 ${isException ? 'text-red-500' : 'text-gray-500'}`} />
                                  <span className="text-sm">{workoutExercise.reps}</span>
                                </div>
                                {workoutExercise.weight !== null && workoutExercise.weight !== undefined && (
                                  <div className="flex items-center">
                                    <Weight size={16} className={`mr-1 ${isException ? 'text-red-500' : 'text-gray-500'}`} />
                                    <span className="text-sm">{workoutExercise.weight} кг</span>
                                  </div>
                                )}
                              </div>
                              

                              
                              {/* Кнопки действий в одной строке */}
                              <div className="flex items-center justify-between border-t border-gray-100 pt-2 mt-2">
                                {/* Кнопка отметки выполнения - только иконка */}
                                {selectedClient && (
                                  <button
                                    onClick={() => handleMarkExerciseCompleted(workoutExercise.id, selectedClient.id)}
                                    className={`p-2 rounded-md border transition-all duration-200 flex items-center justify-center ${
                                      isExerciseCompleted(workoutExercise.id, selectedClient.id)
                                        ? 'text-green-700 bg-green-100 border-green-300'
                                        : 'text-gray-500 bg-gray-50 border-gray-200'
                                    }`}
                                    title="Отметить как выполненное"
                                  >
                                    <Check size={18} />
                                  </button>
                                )}
                                
                                {/* Кнопки управления порядком */}
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => handleMoveExerciseUp(workoutExercise)}
                                    disabled={index === 0}
                                    className={`p-2 rounded-md transition-colors ${
                                      index === 0
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                    title="Переместить вверх"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleMoveExerciseDown(workoutExercise)}
                                    disabled={index === clientExercises.length - 1}
                                    className={`p-2 rounded-md transition-colors ${
                                      index === clientExercises.length - 1
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                    title="Переместить вниз"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                </div>
                                
                                {/* Кнопки редактирования и удаления */}
                                <div className="flex items-center space-x-1">
                                  {/* Кнопка исключения */}
                                  {selectedClient && workoutExercise.exercise && (
                                    <button
                                      onClick={() => handleAddException(workoutExercise.exercise!)}
                                      className={`p-2 rounded-md transition-colors ${
                                        hasException(workoutExercise.exercise.id)
                                          ? 'text-green-700 bg-green-100 border border-green-300 hover:bg-green-200'
                                          : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                                      }`}
                                      title={
                                        hasException(workoutExercise.exercise.id)
                                          ? `Отменить исключение: ${getExceptionReason(workoutExercise.exercise.id)}`
                                          : 'Добавить в исключения'
                                      }
                                    >
                                      <AlertTriangle size={16} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditExercise(workoutExercise)}
                                    disabled={isUpdating}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md disabled:opacity-50 transition-colors"
                                    title="Изменить параметры упражнения"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteExercise(workoutExercise.id)}
                                    disabled={isDeleting}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md disabled:opacity-50 transition-colors"
                                    title="Удалить упражнение"
                                  >
                                    {isDeleting ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                    ) : (
                                      <Trash2 size={16} />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Информация о тренировке - в самом конце */}
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Информация о тренировке</h2>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-600 space-y-1 sm:space-y-0">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2 text-gray-400 flex-shrink-0" />
                  <span className="font-medium">{date}</span>
                </div>
                <div className="flex items-center sm:ml-2">
                  <span className="text-gray-500">в {time}</span>
                </div>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Clock size={16} className="mr-2 text-gray-400" />
                {workout.duration_minutes} минут
              </div>
              <div className="text-sm text-gray-600">
                <div className="flex items-center mb-2">
                  <Users size={16} className="mr-2 text-gray-400 flex-shrink-0" />
                  <span className="font-medium text-gray-700">Клиенты:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {workout.clients.map((client) => (
                    <span
                      key={client.id}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {client.first_name} {client.last_name}
                    </span>
                  ))}
                </div>
              </div>
              {workout.description && (
                <div className="text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-1">Описание:</p>
                  <p>{workout.description}</p>
                </div>
              )}
              {workout.notes && (
                <div className="text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-1">Заметки:</p>
                  <p>{workout.notes}</p>
                </div>
              )}
              <div className="flex items-center">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  workout.is_completed 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {workout.is_completed ? 'Завершена' : 'В процессе'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно добавления упражнения */}
      {isAddingExercise && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4">
                Добавить упражнение для {selectedClient?.first_name} {selectedClient?.last_name}
              </h2>
              <button
                onClick={() => setIsAddingExercise(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              {/* Предупреждение о неактивном клиенте */}
              {selectedClient && !selectedClient.is_active && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Клиент неактивен
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          Нельзя добавлять упражнения для неактивного клиента. 
                          Сначала активируйте клиента в разделе "Клиенты".
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Выбор групп мышц */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Группы мышц
                  </label>
                  {(selectedMuscleGroups.length > 0 || onlyMine) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMuscleGroups([]);
                        setOnlyMine(false);
                        setSelectedExercise(null);
                        setExerciseSearchTerm('');
                        setShowExerciseDropdown(false);
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Сбросить фильтры
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {muscleGroups.map((group) => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleMuscleGroupToggle(group.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        selectedMuscleGroups.includes(group.id)
                          ? 'bg-blue-100 text-blue-800 border-blue-300'
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Фильтр "Только мои" */}
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onlyMine}
                    onChange={(e) => {
                      setOnlyMine(e.target.checked);
                      setSelectedExercise(null);
                      setExerciseSearchTerm('');
                      setShowExerciseDropdown(false);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Только мои упражнения</span>
                </label>
              </div>

              {/* Поиск упражнения */}
              <div className="relative" ref={exerciseDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Упражнение *
                </label>
                <input
                  type="text"
                  value={exerciseSearchTerm}
                  onChange={(e) => handleExerciseSearchChange(e.target.value)}
                  onFocus={() => setShowExerciseDropdown(true)}
                  placeholder="Начните вводить название упражнения..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                
                {/* Выпадающий список упражнений */}
                {showExerciseDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredExercises.length > 0 ? (
                      filteredExercises.map((exercise) => {
                        const isException = selectedClient && hasException(exercise.id);
                        const exceptionReason = isException ? getExceptionReason(exercise.id) : null;
                        
                        return (
                          <div 
                            key={exercise.id} 
                            className={`flex items-center justify-between px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 ${
                              isException ? 'bg-red-50 border-l-4 border-red-400' : ''
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => handleExerciseSelect(exercise)}
                              className="flex-1 text-left focus:outline-none"
                            >
                              <div className={`font-medium ${isException ? 'text-red-700' : 'text-gray-900'}`}>
                                {exercise.name}
                              </div>
                              {isException && exceptionReason && (
                                <div className="text-xs text-red-600 mt-1">
                                  Исключено: {exceptionReason}
                                </div>
                              )}
                            </button>
                            <div className="flex items-center space-x-1">
                              {isException && (
                                <div className="text-red-500" title={`Исключено: ${exceptionReason}`}>
                                  <AlertTriangle size={16} />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowExerciseInfo(exercise);
                                }}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors"
                                title="Информация об упражнении"
                              >
                                <Info size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        {exerciseSearchTerm 
                          ? 'Упражнения не найдены' 
                          : 'Выберите группы мышц или введите название упражнения'
                        }
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Параметры упражнения */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Подходы *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={exerciseForm.sets}
                    onChange={(e) => setExerciseForm(prev => ({ ...prev, sets: Number(e.target.value) }))}
                    onFocus={handleInputFocus}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Повторения *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={exerciseForm.reps}
                    onChange={(e) => setExerciseForm(prev => ({ ...prev, reps: Number(e.target.value) }))}
                    onFocus={handleInputFocus}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Вес (кг)
                  </label>
                  <WeightAdjuster
                    weight={exerciseForm.weight}
                    onWeightChange={(weight) => setExerciseForm(prev => ({ ...prev, weight }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Отдых (сек)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={exerciseForm.rest_seconds}
                    onChange={(e) => setExerciseForm(prev => ({ ...prev, rest_seconds: Number(e.target.value) }))}
                    onFocus={handleInputFocus}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Заметки */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Заметки
                </label>
                <textarea
                  value={exerciseForm.notes}
                  onChange={(e) => setExerciseForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Дополнительные заметки..."
                />
              </div>
            </div>

            {/* Кнопки */}
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 p-4 sm:p-6 border-t border-gray-200">
              <button
                onClick={() => setIsAddingExercise(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                Отмена
              </button>
              <button
                onClick={handleAddExercise}
                disabled={!selectedExercise || isAdding || isCreatingLog || (selectedClient && !selectedClient.is_active)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                {(isAdding || isCreatingLog) ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Добавление...</span>
                  </>
                ) : (
                  <span>Добавить</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно информации об упражнении */}
      <ExerciseInfoModal
        exercise={selectedExerciseForInfo}
        isOpen={isExerciseInfoModalOpen}
        onClose={() => {
          setIsExerciseInfoModalOpen(false);
          setSelectedExerciseForInfo(null);
        }}
      />

      {/* Модальное окно редактирования упражнения */}
      <EditWorkoutExerciseModal
        workoutExercise={selectedExerciseForEdit}
        isOpen={isEditExerciseModalOpen}
        onClose={() => {
          setIsEditExerciseModalOpen(false);
          setSelectedExerciseForEdit(null);
        }}
        onSave={handleSaveExerciseChanges}
        isLoading={isUpdating}
      />

      {/* Модальное окно статистики упражнения */}
      <ExerciseStatsModal
        exercise={selectedExerciseForStats}
        client={selectedClient}
        exerciseLogs={exerciseStatsLogs}
        isOpen={isExerciseStatsModalOpen}
        onClose={() => {
          setIsExerciseStatsModalOpen(false);
          setSelectedExerciseForStats(null);
          setExerciseStatsLogs([]);
        }}
        isLoading={isLoadingStats}
      />

      {/* Модальное окно копирования упражнений */}
      <CopyExercisesModal
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        onSubmit={async (clientIds) => {
          if (!selectedClient) return;
          setIsCopyLoading(true);
          try {
            // Используем новый API endpoint для копирования упражнений
            const result = await apiService.copyExercisesBetweenClients(
              workout.id,
              selectedClient.id,
              clientIds
            );
            toast.success(result.message);
            // Обновляем данные тренировки
            refetch();
          } catch (e) {
            toast.error('Ошибка при копировании упражнений');
          } finally {
            setIsCopyLoading(false);
            setIsCopyModalOpen(false);
          }
        }}
        clients={workout.clients}
        excludeClientId={selectedClient?.id || 0}
        isLoading={isCopyLoading}
      />

      {/* Модальное окно прошлых упражнений */}
      {isPastExercisesModalOpen && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4">
                Предыдущие тренировки для {selectedClient.first_name} {selectedClient.last_name}
              </h2>
              <button
                onClick={() => setIsPastExercisesModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {isLoadingPastExercises ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Загрузка предыдущих тренировок...</span>
                </div>
              ) : pastExercises.length === 0 ? (
                <div className="text-center py-8">
                  <History size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Нет предыдущих тренировок
                  </h3>
                  <p className="text-gray-500">
                    У {selectedClient.first_name} пока нет завершенных тренировок для отображения в истории
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {pastExercises.map((workout, workoutIndex) => (
                    <div key={workout.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-900">
                          {workout.name}
                        </h3>
                        <div className="text-xs text-gray-500">
                          {format(new Date(workout.date), 'dd MMMM yyyy', { locale: ru })}
                        </div>
                      </div>
                      
                      {workout.workout_exercises && workout.workout_exercises.length > 0 ? (
                        <div className="space-y-2">
                          {workout.workout_exercises
                            .filter((exercise: WorkoutExercise) => 
                              exercise.exercise_logs?.some((log: ExerciseLog) => log.client_id === selectedClient.id)
                            )
                            .map((workoutExercise: WorkoutExercise, exerciseIndex: number) => {
                              const clientLog = workoutExercise.exercise_logs?.find(
                                (log: ExerciseLog) => log.client_id === selectedClient.id
                              );
                              return (
                                <div key={workoutExercise.id} className="p-2 bg-gray-50 rounded-md">
                                  <div className="text-sm font-medium text-gray-900">
                                    {exerciseIndex + 1}. {workoutExercise.exercise?.name || 'Упражнение удалено'}
                                  </div>
                                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-600">
                                    <div className="flex items-center">
                                      <Layers size={12} className="text-gray-500 mr-1" />
                                      <span>{clientLog?.completed_sets || workoutExercise.sets}</span>
                                    </div>
                                    <div className="flex items-center">
                                      <Repeat size={12} className="text-gray-500 mr-1" />
                                      <span>{clientLog?.completed_reps || workoutExercise.reps}</span>
                                    </div>
                                    {(clientLog?.actual_weight !== null && clientLog?.actual_weight !== undefined) || (workoutExercise.weight !== null && workoutExercise.weight !== undefined) ? (
                                      <div className="flex items-center">
                                        <Weight size={12} className="text-gray-500 mr-1" />
                                        <span>{clientLog?.actual_weight !== null && clientLog?.actual_weight !== undefined ? clientLog.actual_weight : workoutExercise.weight} кг</span>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">
                          В этой тренировке нет упражнений
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно исключений */}
      {isAddExceptionModalOpen && selectedExerciseForException && (
        <AddExceptionModal
          isOpen={isAddExceptionModalOpen}
          exercise={selectedExerciseForException}
          clientName={selectedClient?.first_name || ''}
          onClose={handleCloseExceptionModal}
          onSubmit={handleSubmitException}
          isLoading={isCreatingException}
        />
      )}

      {/* Модальное окно сохранения шаблона */}
      <SaveTemplateModal
        isOpen={isSaveTemplateModalOpen}
        onClose={handleCloseSaveTemplateModal}
        onSave={handleSaveTemplateSubmit}
        exercises={clientExercises}
        clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : ''}
        isLoading={isSavingTemplate}
      />

      {/* Модальное окно импорта шаблона */}
      <ImportTemplateModal
        isOpen={isImportTemplateModalOpen}
        onClose={handleCloseImportTemplateModal}
        workoutId={Number(id)}
        clientId={selectedClient?.id || 0}
        clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : ''}
        existingExerciseIds={clientExercises.map(ex => ex.exercise_id).filter(Boolean) as number[]}
        onImportSuccess={async () => {
          // Показываем индикатор загрузки
          setIsUpdatingAfterImport(true);
          
          // Показываем сообщение об успешном импорте
          toast.success('Шаблон импортирован! Загружаем новые упражнения...');
          
          try {
            // Сначала принудительно перезагружаем данные тренировки
            await refetch();
            
            // Небольшая задержка для гарантии обновления данных на сервере
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Перезагружаем упражнения клиента
            if (selectedClient) {
              await loadClientExercises();
            }
          } catch (error) {
            console.error('Ошибка при обновлении данных:', error);
            toast.error('Ошибка при загрузке новых упражнений');
          } finally {
            // Скрываем индикатор загрузки
            setIsUpdatingAfterImport(false);
          }
        }}
      />
    </div>
  );
};

export default WorkoutPage; 